import {useSearchParams} from 'react-router-dom';
import {apiRequest} from '@/lib/api';
import {useDataQuery} from '@/hooks/useDataQuery';
import { useState } from "react";
import { useMessages } from "@/hooks/useComms";
import LoadingState from "../components/LoadingState";
import ErrorState from "../components/ErrorState";

export default function Mesajlar() {
  const { data, loading, error, reload, sendMessage, markRead, sending, sendError } = useMessages();
  const [params]=useSearchParams();const teacherId=params.get('teacher')??'';
  const target=useDataQuery(()=>teacherId?apiRequest<{id:string;name:string}>(`/learning/teacher?id=${encodeURIComponent(teacherId)}`):Promise.resolve(null),[teacherId]);
  const [directError,setDirectError]=useState('');const [directBusy,setDirectBusy]=useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;

  const list = data ?? [];
  const direct=target.data?{id:'new:'+target.data.id,peerId:target.data.id,name:target.data.name,role:'Öğretmen',avatar:'',lastMessage:'',time:'',unread:0,messages:[]}:null;
  const active = activeId?list.find(c=>c.id===activeId)??null:teacherId?(list.find(c=>c.peerId===target.data?.id)??direct):list[0]??null;

  const openConversation = (id: string) => {
    setActiveId(id);
    markRead(id);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !active || sending) return;
    if(active.id.startsWith('new:')){if(directBusy)return;setDirectBusy(true);try{await apiRequest('/mobile/message',{method:'POST',body:{peer_id:active.peerId,text:newMessage.trim(),request_id:crypto.randomUUID()}});setNewMessage('');reload();setDirectError('');}catch(e){setDirectError(e instanceof Error?e.message:'Mesaj gönderilemedi.');}finally{setDirectBusy(false);}}else if (await sendMessage(active.id, newMessage.trim())) setNewMessage("");
  };

  return (
    <div className="space-y-6">
      {target.loading&&teacherId&&<p role="status">Öğretmen hesabı açılıyor…</p>}{(target.error||directError)&&<p role="alert">{target.error||directError}</p>}
      {sendError && <p role="alert" className="text-red-700">{sendError}</p>}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Mesajlar</h1>
        <p className="text-sm md:text-base text-foreground-600 mt-1">
          Öğretmenlerinle görüşmelerin.
        </p>
      </div>

      <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr] min-h-[480px]">
        {/* Sohbet listesi */}
        <div className="border-b md:border-b-0 md:border-r border-background-200">
          {list.map((conv) => (
            <button
              key={conv.id}
              type="button"
              onClick={() => openConversation(conv.id)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-colors ${
                active?.id === conv.id ? "bg-primary-50" : "hover:bg-background-100"
              }`}
            >
              <div className="relative w-11 h-11 rounded-full overflow-hidden bg-background-100 shrink-0">
                <img src={conv.avatar} alt={conv.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-sm text-foreground-950 truncate">{conv.name}</p>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{conv.time}</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-foreground-500 truncate">{conv.lastMessage}</p>
                  {conv.unread > 0 && (
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-[10px] font-semibold shrink-0">
                      {conv.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Konuşma */}
        {active ? (
          <div className="flex flex-col">
            <div className="flex items-center gap-3 px-5 py-3.5 border-b border-background-200">
              <div className="w-10 h-10 rounded-full overflow-hidden bg-background-100 shrink-0">
                <img src={active.avatar} alt={active.name} className="w-full h-full object-cover object-top" />
              </div>
              <div>
                <p className="font-semibold text-sm text-foreground-950">{active.name}</p>
                <p className="text-xs text-foreground-500">{active.role}</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-background-50">
              {active.messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.isMe ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[75%] rounded-lg px-3.5 py-2.5 ${
                      msg.isMe
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-800"
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{msg.text}</p>
                    <p className={`text-[10px] mt-1 ${msg.isMe ? "opacity-70" : "text-foreground-400"}`}>
                      {msg.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-background-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  placeholder="Mesaj yaz..."
                  className="flex-1 h-10 px-3 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400"
                />
                <button
                  type="button"
                  onClick={handleSend}
                  className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-send-plane-2-line" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center">
            <p className="text-sm text-foreground-500">Bir görüşme seç.</p>
          </div>
        )}
      </div>
    </div>
  );
}
